'use client';

import { ChevronRight } from 'lucide-react';
import { SmartLink } from '@/app/_components/core/smart-link';
import type { FabCta } from '@/app/_components/shared/fab/fab.types';

interface FabContactItemProps {
  cta: FabCta;
  onNavigate?: () => void;
  trackingPrefix?: string;
}

export function FabContactItem({ cta, onNavigate, trackingPrefix }: FabContactItemProps) {
  const isDisabled = cta.disabled === true;

  const baseClasses = [
    'flex items-center gap-3 p-4 transition-colors',
    isDisabled
      ? 'cursor-not-allowed opacity-50'
      : 'cursor-pointer hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
  ].join(' ');

  const content = (
    <>
      {cta.icon?.url && (
        <span className="grid size-10 shrink-0 place-items-center rounded-md bg-fab-icon-bg">
          {/* eslint-disable-next-line @next/next/no-img-element -- small CMS icon (20px), no next/image benefit */}
          <img
            src={cta.icon.url}
            alt={cta.icon.altText ?? ''}
            title={cta.icon.title ?? undefined}
            className="size-5"
          />
        </span>
      )}

      <span className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
        <span className="truncate text-base font-semibold leading-none text-foreground">
          {cta.title}
        </span>
        {cta.subtitle && (
          <span className="truncate text-sm font-normal text-muted-foreground">
            {cta.subtitle}
          </span>
        )}
      </span>

      <ChevronRight
        className="size-4 shrink-0 text-input"
        aria-hidden
      />
    </>
  );

  if (isDisabled) {
    return (
      <span className={baseClasses} aria-disabled="true">
        {content}
      </span>
    );
  }

  return (
    <SmartLink
      href={cta.linkUrl}
      target={cta.linkTarget}
      download={cta.download}
      onClick={onNavigate}
      className={baseClasses}
      data-track-id={trackingPrefix}
    >
      {content}
    </SmartLink>
  );
}